import { useState } from "react";
import { Download, X, Share2, PlusSquare } from "lucide-react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

/**
 * Botão "Instalar app" para colocar em menus/headers.
 * - Se o navegador tem prompt nativo (Android/Chrome): clica e dispara o prompt.
 * - Se é iOS Safari: abre modal com instruções manuais ("Compartilhar → Adicionar à Tela de Início").
 * - Se já está instalado (standalone) ou não é um contexto compatível: não renderiza.
 */
export default function InstallAppButton({
  className = "",
  labelIdle = "Instalar app",
  testid = "install-app-btn",
}) {
  const { available, isIOS, hasNativePrompt, install } = useInstallPrompt();
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [installing, setInstalling] = useState(false);

  if (!available) return null;

  const onClick = async () => {
    if (hasNativePrompt) {
      setInstalling(true);
      try { await install(); } finally { setInstalling(false); }
      return;
    }
    if (isIOS) setShowIOSModal(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={installing}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bbh-btn-primary disabled:opacity-60 ${className}`}
      >
        <Download className="w-3.5 h-3.5" />
        {installing ? "Instalando…" : labelIdle}
      </button>

      {showIOSModal && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSModal(false)}
          data-testid="install-ios-modal"
        >
          <div
            className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-sm p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-md bg-[#14243D] text-white flex items-center justify-center">
                <Download className="w-5 h-5" />
              </div>
              <button
                onClick={() => setShowIOSModal(false)}
                className="p-1 text-zinc-400 hover:text-zinc-700"
                aria-label="Fechar"
                data-testid="install-ios-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="font-heading text-lg font-bold mb-1">Instalar no iPhone</div>
            <p className="text-sm text-zinc-600 mb-4">
              O Safari do iPhone não instala apps automaticamente. Basta seguir dois passos:
            </p>
            <ol className="space-y-3 text-sm text-zinc-800">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-zinc-500"><Share2 className="w-4 h-4" /></span>
                <span>Toque no botão <b>Compartilhar</b> (barra inferior do Safari).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-zinc-500"><PlusSquare className="w-4 h-4" /></span>
                <span>Escolha <b>Adicionar à Tela de Início</b> e confirme em <b>Adicionar</b>.</span>
              </li>
            </ol>
            <button
              onClick={() => setShowIOSModal(false)}
              className="mt-5 w-full px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold"
              data-testid="install-ios-ok"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
